/* storybook-check-ignore */
import { useEffect, useState } from 'react';

import {
  Box,
  Dialog,
  getCSSVariable,
  Image,
  PressableBox,
  Text,
  useMedia,
} from '@opendoor/bricks-next';
import { IconShieldLockFilled, IconTruckFilled } from '@tabler/icons-react';

export const BuyWithConfidence = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { smallerThanMD } = useMedia();
  const [isVerySmall, setIsVerySmall] = useState(false);

  useEffect(() => {
    if (!isModalOpen) return;

    const checkWidth = () => {
      // iPhone SE and iPhone 13 Mini screen resolution is right at 375,
      // which the current breakpoints don't handle.
      setIsVerySmall(window.innerWidth <= 375);
    };

    checkWidth();
    window.addEventListener('resize', checkWidth);

    return () => window.removeEventListener('resize', checkWidth);
  }, [isModalOpen]);

  const getModalTitleTypography = () => {
    if (!smallerThanMD) return '$headerSmall';
    return isVerySmall ? '$subheaderSmall' : '$subheaderLarge';
  };

  const getModalSubtitleTypography = () => {
    if (!smallerThanMD) return '$bodyLarge';
    return isVerySmall ? '$bodySmall' : '$bodyMedium';
  };

  return (
    <>
      <Box width="100%">
        <PressableBox
          onPress={() => setIsModalOpen(true)}
          aria-label="Open The Opendoor Guarantee details"
          analyticsName="marketplace-buy-with-confidence-button"
          borderWidth={1}
          borderRadius="$6x"
          paddingTop="$12x"
          paddingRight="$12x"
          paddingBottom="$12x"
          paddingLeft="$10x"
          backgroundColor="$backgroundPrimary"
          flexDirection="row"
          gap="$8x"
          alignItems="center"
          justifyContent="flex-start"
          width="100%"
          style={{
            borderColor: 'rgba(0, 65, 231, 0.3)',
            background:
              'linear-gradient(0deg, rgba(0, 65, 231, 0.03) 0%, rgba(0, 65, 231, 0.03) 100%), var(--sem-color-background-primary, #FFF)',
            boxShadow:
              '0 2px 12px 0 rgba(0, 65, 231, 0.04), 0 10px 22px 0 rgba(0, 65, 231, 0.04), 0 0 0 2px rgba(0, 65, 231, 0.10), 0 1px 6px 0 rgba(0, 65, 231, 0.20)',
          }}
          data-testid="marketplace-buy-with-confidence-button"
        >
          <Image
            source={{
              uri: 'https://images.opendoor.com/source/s3/imgdrop-production/2025-12-1764882992133-33358.png?preset=square-128&service=cosmos-cache-friendly&dpr=2',
              width: 56,
              height: 56,
            }}
            alt="Opendoor Guarantee"
          />
          <Box flexDirection="column" gap="$2x" alignItems="flex-start" flex={1}>
            <Text tag="p" typography="$labelMedium" color="$contentPrimary" textAlign="left">
              The Opendoor Guarantee
            </Text>
            <Text tag="p" typography="$bodySmall" color="$contentSecondary" textAlign="left">
              Included with this home.{' '}
              <Text
                tag="span"
                typography="$bodySmall"
                color="$contentSecondary"
                style={{
                  borderBottom: '1px solid',
                  borderColor: 'currentColor',
                  whiteSpace: 'nowrap',
                  display: 'inline-block',
                }}
              >
                Learn more
              </Text>
            </Text>
          </Box>
        </PressableBox>
      </Box>

      <Dialog isOpen={isModalOpen} setIsOpen={setIsModalOpen}>
        <Dialog.Content
          title="The Opendoor Guarantee"
          description="Included with every home"
          hideTitle
          hideDescription
        >
          <Box maxWidth={552} marginHorizontal="auto" width="100%">
            <Box
              flexDirection="column"
              gap="$12x"
              alignItems="center"
              paddingTop={0}
              paddingBottom={0}
            >
              <Box flexDirection="column" gap="$4x" alignItems="center" width="100%">
                <Image
                  source={{
                    uri: isVerySmall
                      ? 'https://images.opendoor.com/source/s3/imgdrop-production/2025-12-1764962388442-16990.png?preset=square-128&service=cosmos-cache-friendly&dpr=2'
                      : 'https://images.opendoor.com/source/s3/imgdrop-production/2025-12-1764882992132-81635.png?preset=square-128&service=cosmos-cache-friendly&dpr=2',
                    width: isVerySmall ? 60 : 82,
                    height: isVerySmall ? 60 : 82,
                  }}
                  alt="Opendoor Guarantee"
                />
                <Box flexDirection="column" gap="$2x" alignItems="center" width="100%">
                  <Text
                    tag="h2"
                    typography={getModalTitleTypography()}
                    color="$contentPrimary"
                    textAlign="center"
                  >
                    The Opendoor Guarantee
                  </Text>
                  <Text
                    tag="p"
                    typography={getModalSubtitleTypography()}
                    color="$contentTertiary"
                    textAlign="center"
                  >
                    Included with this home
                  </Text>
                </Box>
              </Box>

              <Box flexDirection="column" gap="$12x" width="100%">
                <Box
                  backgroundColor="$backgroundSecondary"
                  padding="$6x"
                  borderRadius="$4x"
                  flexDirection="row"
                  gap="$4x"
                  alignItems="flex-start"
                >
                  <Box flexShrink={0}>
                    <IconTruckFilled
                      size={24}
                      color={getCSSVariable('$contentAccentBrand', 'color')}
                    />
                  </Box>
                  <Box flexDirection="column" gap="$2x" flex={1}>
                    <Text
                      tag="p"
                      typography={smallerThanMD ? '$labelSmall' : '$labelMedium'}
                      color="$contentPrimary"
                    >
                      Early Move-In
                    </Text>
                    <Text
                      tag="p"
                      typography={smallerThanMD ? '$bodySmall' : '$bodyMedium'}
                      color="$contentSecondary"
                    >
                      Move in up to 7 days early, perfect for scheduling movers and furniture
                      deliveries.
                    </Text>
                  </Box>
                </Box>

                <Box
                  backgroundColor="$backgroundSecondary"
                  padding="$6x"
                  borderRadius="$4x"
                  flexDirection="row"
                  gap="$4x"
                  alignItems="flex-start"
                >
                  <Box flexShrink={0}>
                    <IconShieldLockFilled
                      size={24}
                      color={getCSSVariable('$contentAccentBrand', 'color')}
                    />
                  </Box>
                  <Box flexDirection="column" gap="$2x" flex={1}>
                    <Text
                      tag="p"
                      typography={smallerThanMD ? '$labelSmall' : '$labelMedium'}
                      color="$contentPrimary"
                    >
                      100-Day Warranty
                    </Text>
                    <Text
                      tag="p"
                      typography={smallerThanMD ? '$bodySmall' : '$bodyMedium'}
                      color="$contentSecondary"
                    >
                      Peace of mind with coverage on key systems and major appliances for your first
                      100 days.
                    </Text>
                  </Box>
                </Box>

                <Box
                  backgroundColor="$backgroundSecondary"
                  padding="$6x"
                  borderRadius="$4x"
                  flexDirection="row"
                  gap="$4x"
                  alignItems="flex-start"
                >
                  <Box flexShrink={0}>
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M16.9756 3.54395C18.3563 3.54395 19.4756 4.66323 19.4756 6.04395V11.2588H11.8701L15.3633 7.7666C15.6561 7.4737 15.6562 6.99795 15.3633 6.70508C15.0704 6.41227 14.5946 6.41227 14.3018 6.70508L9.5293 11.4785C9.23642 11.7714 9.23646 12.2462 9.5293 12.5391L14.3018 17.3125C14.5947 17.6054 15.0704 17.6054 15.3633 17.3125C15.6561 17.0196 15.6562 16.5438 15.3633 16.251L11.8701 12.7588H19.4756V17.9834C19.4756 19.3641 18.3563 20.4834 16.9756 20.4834H7.02734C5.64663 20.4834 4.52734 19.3641 4.52734 17.9834V6.04395C4.52734 4.66323 5.64663 3.54395 7.02734 3.54395H16.9756Z"
                        fill="#0041E7"
                      />
                    </svg>
                  </Box>
                  <Box flexDirection="column" gap="$2x" flex={1}>
                    <Text
                      tag="p"
                      typography={smallerThanMD ? '$labelSmall' : '$labelMedium'}
                      color="$contentPrimary"
                    >
                      Free Cancellation
                    </Text>
                    <Text
                      tag="p"
                      typography={smallerThanMD ? '$bodySmall' : '$bodyMedium'}
                      color="$contentSecondary"
                    >
                      If you don’t love your new home, cancel anytime before closing with zero
                      penalty.
                    </Text>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        </Dialog.Content>
      </Dialog>
    </>
  );
};
